/**
 * sharedWipCache — a short-lived, module-level cache in front of
 * productionGridService.listWips() and dispatchService.listSlips().
 *
 * Dispatch·BQC3, Receiving·BQC4 and QC·BQC5 each independently fetch the
 * FULL iSyn_WIPs list (thousands of rows, every WIP_SELECT column, with the
 * BatchID $expand) and the full iSyn2_DispatchSlips list on every mount —
 * three separate full downloads of the same data every time a planner hops
 * Dispatch → Receiving → QC. Neither list is safe to trim (every field is
 * read by at least one of the three screens) and neither is safe to filter
 * server-side without a product decision about which WIPs a station screen
 * still needs to search — so the fix here is narrower: don't re-download
 * what was already downloaded moments ago.
 *
 * Why a short TTL is safe rather than a correctness risk:
 *  - iSyn_WIPs.currentStage and every other WIP field is Excel-owned — these
 *    screens never write to iSyn_WIPs directly, only the periodic workbook
 *    import does (a much longer cadence than this cache's TTL), so a WIP row
 *    the cache is serving cannot go stale from anything a user does on these
 *    screens while looking at them.
 *  - A slip a user creates/amends/cancels on Dispatch is applied to that
 *    screen's own `slips` state directly (setSlips optimistic update, not a
 *    re-fetch) — the cache is never in the way of seeing your own action.
 *    Another screen reading the cache within the TTL window simply sees
 *    slips as they stood up to ~20s ago, the same staleness a screen that
 *    fetched fresh a few seconds before your action would already have shown
 *    under the old one-fetch-per-mount code.
 *
 * force=true (wired to each screen's own explicit Refresh action / `tick`
 * bump) always bypasses the cache and refreshes it for the next reader too.
 */
import { productionGridService, IProductionWip } from "./ProductionGridService";
import { dispatchService, ISlip } from "./DispatchService";
import { qcService, type IQcScan } from "./QcService";
import { receivingService } from "./ReceivingService";
import type { IReceivingScan } from "../../modules/mfg-workcenters/receivingLedger";

/**
 * TWO TTLs, because the two lists have opposite owners.
 *
 * iSyn_WIPs is EXCEL-OWNED. No station screen writes to it; only the workbook
 * import does — and since 2026-09-08 the scheduled sync is stopped, so it now
 * changes only when somebody presses Import by hand. Twenty seconds was
 * calibrated for a periodic importer that no longer runs. A planner walking
 * Dispatch → Receiving → QC over a couple of minutes was blowing straight
 * through it and re-downloading thousands of rows at every hop, which is the
 * slowness Mikko reported (2026-09-08: "when i navigate from certain BQC
 * there's delay or slow loading").
 *
 * iSyn2_DispatchSlips is APP-OWNED and changes all day. A dispatcher issues a
 * slip on BQC3 and QC opens two minutes later expecting to see it, so this one
 * stays short — it exists to absorb a quick hop, not to hold data.
 *
 * Refresh on every screen forces past both, so a long WIP TTL can never leave
 * somebody stuck: there is always a visible way to go and look again.
 */
const WIP_TTL_MS = 5 * 60_000;
const SLIP_TTL_MS = 20_000;
/** iSyn2_QcScans, same bargain as slips — app-owned and moving all day. See
 *  listQcScansCached for why the size did not buy it a longer one. */
const QC_SCAN_TTL_MS = 20_000;
/** iSyn2_ReceivingScans, the same bargain again — see listReceivingScansCached. */
const RECEIVING_TTL_MS = 20_000;
/**
 * How old a PERSISTED QC snapshot may be before a cold page ignores it.
 *
 * Mikko, 2026-09-14: "BQC5 should now be included in cache memory, because i
 * noticed its the only one that last to take load". He is right, and the
 * reason is arithmetic: iSyn2_QcScans is 36,000 rows and eight paged requests,
 * and it was the one big list with no snapshot behind it — so a hard refresh
 * on BQC5 always paid for a cold read of the whole thing while BQC2 and BQC4
 * painted from disk.
 *
 * A DAY, not the month iSyn_WIPs gets, and the difference is ownership. WIPs
 * are import-owned, so a week-old snapshot is not stale but exact. This list
 * is APP-OWNED and moves all day, so the snapshot is only ever a first paint:
 * a live read starts in the same breath and `onFresh` replaces it seconds
 * later. A day is long enough to cover the morning refresh that hurt, short
 * enough that a laptop opened next week starts clean.
 *
 * IT IS NEVER THE AUTHORITY, and two existing rules are what make that true
 * rather than hopeful. Writers (QcImportService, QcOutboxService,
 * TestDataService) read qcService.listQcScans() DIRECTLY and are meant to, so
 * no snapshot can cause a double insert or a missed export. And every station
 * forces past both caches after its own writes — BQC5 calls this with
 * `tick > 0` the moment an inspector records anything.
 */
const QC_PERSIST_MAX_MS = 24 * 60 * 60_000;

interface CacheEntry<T> {
  data: T;
  at: number;
}

let wipsEntry: CacheEntry<IProductionWip[]> | null = null;
let slipsEntry: CacheEntry<ISlip[]> | null = null;
let qcScansEntry: CacheEntry<IQcScan[]> | null = null;
let receivingEntry: CacheEntry<IReceivingScan[]> | null = null;
/** Reads already in flight to revalidate a stale entry — one per list, shared by every caller. */
let wipsRevalidate: Promise<IProductionWip[]> | null = null;
let qcRevalidate: Promise<IQcScan[]> | null = null;
let receivingRevalidate: Promise<IReceivingScan[]> | null = null;

function fresh<T>(entry: CacheEntry<T> | null, ttlMs: number): entry is CacheEntry<T> {
  return !!entry && Date.now() - entry.at < ttlMs;
}


/* ── Surviving a hard refresh ──────────────────────────────────────────────
 *
 * Mikko, 2026-09-09, on the fifth report: "whenever i hard refresh and go to
 * other screen and back again in certain screen, it took so long to load...
 * how about cache memory?"
 *
 * He is right, and the caches above could not help him. They live in the
 * PAGE's memory, so a refresh throws them away and the station starts from
 * nothing every time: ~15,600 WIPs re-read before the table can render at
 * all. That read is the "Loading…" on the screen, and no in-memory TTL can
 * shorten it.
 *
 * So the snapshot is persisted to IndexedDB and served STALE-WHILE-
 * REVALIDATE: a refresh renders from the last snapshot at once, a fresh read
 * runs behind it, and the caller is handed the new list when it lands.
 *
 * WHY THIS IS SAFE AT A SCANNING STATION, which is the only question that
 * matters here. A stale list cannot make a scanner refuse a real bundle:
 * every station already answers a miss with a direct single-WIP lookup
 * against iSyn_WIPs before it refuses anything (see Cutting.tsx's own note —
 * "the local snapshot loads once per page visit... one direct, single-WIP
 * check before refusing"). The snapshot is a head start on rendering, never
 * the authority on whether a bundle exists.
 *
 * And iSyn_WIPs barely moves: it is written ONLY by the Production Grid
 * import, not by any station, so between imports the persisted copy is not
 * stale at all — it is exact. Live scans are read from their own lists, which
 * are never served from here.
 *
 * SLIPS ARE NOT PERSISTED. They are app-owned and change all day; twenty
 * seconds in memory is a different bargain from minutes on disk, and BQC3
 * issuing a slip that BQC4 cannot see is the one staleness that would cost
 * somebody a delivery.
 */

/**
 * AGE IS NOT THE TEST — and the twelve-hour ceiling this used to have was the
 * reason most people never saw the cache at all.
 *
 * Mikko, 2026-09-11: "im the only one have cache memory (quick to load data)".
 * He was right about the symptom and it was never about his account —
 * IndexedDB is per browser, per origin, and nobody is privileged. It was the
 * ceiling. He is in the app all day, so his snapshot was always minutes old
 * and always served. A floor operator who opens it once each morning found a
 * snapshot fourteen hours old, had it thrown away, and waited for a cold read
 * of ~15,600 WIPs — every single day, for ever.
 *
 * So the ceiling is now a sanity bound rather than a freshness test. What
 * makes an old snapshot safe here is not that it is recent but that
 * `iSyn_WIPs` is IMPORT-OWNED: no station writes to it, so between imports a
 * week-old snapshot is not stale, it is exact. And it is never the authority
 * — every screen revalidates behind it, and every scanner already answers a
 * miss with a direct single-WIP lookup before refusing anything.
 *
 * A month is kept only so a laptop that has been in a drawer since a schema
 * change starts clean rather than rendering something unrecognisable.
 */
const PERSIST_MAX_MS = 30 * 24 * 60 * 60_000;
const DB_NAME = "isyn-cache";
const STORE = "snapshots";

function openDb(): Promise<IDBDatabase | null> {
  return new Promise(resolve => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      // A browser with storage blocked never fires either — do not hang the
      // screen waiting for it.
      setTimeout(() => resolve(null), 1500);
    } catch { resolve(null); }
  });
}

/**
 * The keys this database holds. Named in one place because they are a
 * contract with every screen that reads them, and a typo would silently mean
 * "no snapshot" — which looks exactly like a cold first visit and would never
 * be reported as a bug.
 */
export const SNAPSHOT_KEYS = {
  /** productionGridService.listWips() — the stations' full projection. */
  wips: "wips",
  /** listWipsSlim() — the Production Grid's narrow one. A different shape, so
   *  a different key: the two must never be served to each other. */
  gridWips: "grid-wips",
  gridBatches: "grid-batches",
  gridDaily: "grid-daily",
  /** qcService.listQcScans() — see listQcScansCached. */
  qcScans: "qc-scans",
} as const;

export async function readSnapshot<T>(key: string): Promise<CacheEntry<T> | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise(resolve => {
    try {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
      req.onsuccess = () => {
        const v = req.result as CacheEntry<T> | undefined;
        resolve(v && typeof v.at === "number" && v.data ? v : null);
      };
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

export function writeSnapshot<T>(key: string, entry: CacheEntry<T>): void {
  // Fire and forget: a snapshot that fails to persist costs the NEXT load a
  // little time and nothing else, so it must never delay this one.
  void openDb().then(db => {
    if (!db) return;
    try { db.transaction(STORE, "readwrite").objectStore(STORE).put(entry, key); }
    catch { /* quota, private mode — the memory cache still stands */ }
  });
}

/** Drop persisted snapshots — for an explicit Refresh, so "go and look again"
 *  cannot be answered from disk. */
export function clearSnapshots(...keys: string[]): void {
  void openDb().then(db => {
    if (!db) return;
    try {
      const store = db.transaction(STORE, "readwrite").objectStore(STORE);
      for (const k of keys) store.delete(k);
    } catch { /* nothing to clear */ }
  });
}

/** The stations' own snapshot, by its old name — kept so their Refresh keeps
 *  reading as it did. */
export function clearPersistedWips(): void {
  clearSnapshots(WIPS_KEY);
}

/**
 * Forget iSyn_WIPs — in memory AND on disk.
 *
 * The five-minute TTL above is argued from "iSyn_WIPs is Excel-owned; no
 * station screen writes to it". BQC1's board now does: a planner can correct a
 * WIP's set count there. The moment the app itself writes that list, a cache
 * calibrated for a list nobody writes is wrong — a planner would fix a qty,
 * walk to Cutting, and be shown the old figure for the next five minutes on
 * their own edit.
 *
 * So the writer calls this, and the next reader on any screen goes to
 * SharePoint. Cheaper and far more honest than shortening the TTL for
 * everybody to cover an edit that happens a few times a week.
 */
export function invalidateWipsCache(): void {
  wipsEntry = null;
  clearPersistedWips();
}

/**
 * A snapshot worth rendering: present, the right shape, and not absurdly old.
 * Exported so the Production Grid applies exactly this test rather than a
 * second opinion about what counts as usable.
 */
export function usableSnapshot<T>(snap: CacheEntry<T> | null, maxAgeMs = PERSIST_MAX_MS): snap is CacheEntry<T> {
  return !!snap && Date.now() - snap.at < maxAgeMs;
}

const WIPS_KEY = SNAPSHOT_KEYS.wips;
const QC_SCANS_KEY = SNAPSHOT_KEYS.qcScans;

/**
 * Same result as productionGridService.listWips(), served from the shared
 * cache when another BQC3/4/5 screen already fetched it within the TTL — and,
 * after a hard refresh, from the persisted snapshot while a fresh read runs
 * behind it.
 *
 * `onFresh` is how the revalidation reaches the screen. A caller that passes
 * it renders the snapshot immediately and is handed the real list seconds
 * later; one that does not simply gets the snapshot, which is what every
 * caller got before this existed.
 */
export async function listWipsCached(
  force = false,
  onFresh?: (wips: IProductionWip[]) => void,
  /**
   * Each page of a COLD read, as it lands.
   *
   * Only ever called on the path that actually goes to SharePoint — a cache
   * hit and a snapshot both return whole and instantly, so there is nothing to
   * stream. A caller passing this is saying "I would rather show five thousand
   * rows now than thirty-four thousand in seven round trips' time".
   */
  onPage?: (page: IProductionWip[], soFar: number) => void,
): Promise<IProductionWip[]> {
  if (!force && fresh(wipsEntry, WIP_TTL_MS)) return wipsEntry.data;

  if (force) {
    clearPersistedWips();
  } else {
    // Past its TTL is NOT the same as cold. Coming back to a screen after a few
    // minutes found the in-memory entry too old to serve and skipped the
    // snapshot (that path was only for an EMPTY memory), so the page sat on a
    // blocking read of every WIP while a hard refresh — which has no entry and
    // so painted from the snapshot — came up at once (Mikko, 2026-10-05: "when
    // i came back, its lag … i have to hard refresh"). A stale list is served
    // the way the snapshot is: now, with the real one swapped in behind it. Only
    // for a caller that GIVES us a way to swap it in; one that does not still
    // waits for the truth, as it always did.
    let stale: CacheEntry<IProductionWip[]> | null = wipsEntry;
    if (!stale) {
      // Nothing in memory: this is a cold page. Show the last snapshot rather
      // than an empty table, then go and get the truth.
      const snap = await readSnapshot<IProductionWip[]>(WIPS_KEY);
      if (usableSnapshot(snap) && Array.isArray(snap.data) && snap.data.length) stale = snap;
    } else if (!onFresh) {
      stale = null;
    }
    if (stale && Array.isArray(stale.data) && stale.data.length) {
      wipsEntry = stale;
      void (wipsRevalidate ??= productionGridService.listWips()
        .then(data => {
          wipsEntry = { data, at: Date.now() };
          writeSnapshot(WIPS_KEY, wipsEntry);
          return data;
        })
        .finally(() => { wipsRevalidate = null; }))
        .then(data => onFresh?.(data))
        .catch(() => undefined);   // the stale list stands; Refresh is still there
      return stale.data;
    }
  }

  const data = await productionGridService.listWips(onPage);
  wipsEntry = { data, at: Date.now() };
  writeSnapshot(WIPS_KEY, wipsEntry);
  return data;
}

/**
 * The WIPs for SUBCONTRACTORS · PAYROLL: a slim projection read in parallel
 * Id ranges (`listWipsForPayroll`), held in its OWN entry.
 *
 * Deliberately not `listWipsCached`: payroll's Reload forces past the cache,
 * and a forced `listWipsCached` clears the persisted snapshot every BQC
 * station paints from — so one payroll save made the next BQC1 open a cold
 * 34,000-row read. This entry never touches that snapshot, and no stale
 * snapshot is served in its place: a WIP list missing today's new WIPs would
 * leave their passes "not in the grid" and unpriced, which is money. If the
 * full list is already fresh in memory (a BQC screen just read it) that is a
 * superset and is used as is.
 */
let payrollWipsEntry: CacheEntry<IProductionWip[]> | null = null;

export async function listPayrollWipsCached(force = false): Promise<IProductionWip[]> {
  if (!force) {
    if (fresh(wipsEntry, WIP_TTL_MS)) return wipsEntry.data;
    if (fresh(payrollWipsEntry, WIP_TTL_MS)) return payrollWipsEntry.data;
  }
  const data = await productionGridService.listWipsForPayroll();
  payrollWipsEntry = { data, at: Date.now() };
  return data;
}

/**
 * WIP id -> its full SKU CODE ("SS028-MDBLU-S"), off the shared WIP cache.
 *
 * For the payout document, which recovers a garment's colour and size from a
 * line's `wipIds` when the payout has no stored `skuQty` (garmentName.ts,
 * payslipDoc.ts). Built from `listWipsCached`, so it rides the same 5-minute
 * cache and snapshot every BQC screen already fills — a reprint pays for no
 * new read when a station has been open, and one cheap cached read when not.
 * WIP ids are upper-cased, the way the line stores them.
 */
export async function skuByWip(force = false): Promise<Map<string, string>> {
  const wips = await listWipsCached(force);
  const m = new Map<string, string>();
  for (const w of wips) {
    const id = String(w.wipId ?? "").trim().toUpperCase();
    const sku = String(w.sku ?? "").trim();
    if (id && sku) m.set(id, sku);
  }
  return m;
}

/** Same result as dispatchService.listSlips(), served from the shared cache
 * when another BQC3/4/5 screen already fetched it within the TTL. */
export async function listSlipsCached(force = false): Promise<ISlip[]> {
  if (!force && fresh(slipsEntry, SLIP_TTL_MS)) return slipsEntry.data;
  const data = await dispatchService.listSlips();
  slipsEntry = { data, at: Date.now() };
  return data;
}

/**
 * A SEPARATE cache entry from `listSlipsCached`'s, never shared with it —
 * FOR SUBCONTRACTORS · PAYROLL ONLY. `listSlips()` has no `$filter` at all,
 * so every payroll load was a full, unconditioned download of the entire
 * slip archive with nowhere in `sharedWipCache` safe to cache it longer
 * (BQC4 needs the live one fresh — see `listSlipsCached`'s own comment).
 * `dispatchService.listSlipsSince(sinceIso)` narrows the READ itself, so this
 * is a genuinely smaller download, not the same one held longer — safe to
 * add without touching what BQC3/4/5 read, because they never call this.
 *
 * Keyed on `sinceIso` too: a caller that asks for a different window must
 * not be served another window's answer. In practice there is one caller
 * (`useSubconPayroll.ts`) with one constant window, so this rarely matters,
 * but a cache that silently ignores its own key is worse than one more
 * comparison.
 */
let slipsSinceEntry: (CacheEntry<ISlip[]> & { sinceIso: string }) | null = null;

export async function listSlipsSinceCached(sinceIso: string, force = false): Promise<ISlip[]> {
  if (!force && slipsSinceEntry && slipsSinceEntry.sinceIso === sinceIso && fresh(slipsSinceEntry, SLIP_TTL_MS)) {
    return slipsSinceEntry.data;
  }
  const data = await dispatchService.listSlipsSince(sinceIso);
  slipsSinceEntry = { data, at: Date.now(), sinceIso };
  return data;
}

/**
 * Same result as qcService.listQcScans(), shared the way slips are.
 *
 * WHY THIS BECAME WORTH CACHING. `iSyn2_QcScans` held 23 rows until the QC
 * userform backfill ran overnight on 2026-09-14 and made it 36,030. Nine
 * screens read the whole list on mount — the Grid, Control Tower, Dispatch,
 * Receiving, QC, Sewer Metrics, the Surge panel, the workcenter hook and the
 * payroll run — and the list has no `top`, so each of those is eight paged
 * requests of 5,000. A planner walking Dispatch → Receiving → QC was paying
 * for the same 36,030 rows three times in a minute.
 *
 * NOT A DATE WINDOW, though that was the first idea and it is the wrong one.
 * Too much of what reads this list needs ALL of it, and every one of those
 * readers decides something: the importer looks up every `sourceKey` to know
 * what it has already written (window it and a re-run inserts a second copy
 * of everything older), the outbox looks for unsynced rows of any age, the
 * payroll run re-derives periods behind the current one, and Sewer Metrics is
 * lifetime by definition. Same conclusion this file reached for iSyn_WIPs:
 * don't trim the read, stop repeating it.
 *
 * SHORT TTL, AND NOT PERSISTED — the slips bargain, for the slips reason.
 * This list is APP-OWNED and moves all day: BQC5 passing a bundle that BQC4
 * still shows as outstanding is exactly the staleness that costs somebody a
 * delivery. Twenty seconds absorbs a screen hop without holding data. The
 * frozen import history is most of the payload and would persist happily, but
 * splitting the list by age is the same trim this decided against.
 *
 * A WRITER MUST NOT READ THIS. QcImportService, QcOutboxService and
 * TestDataService each read the ledger to decide what to write into it, and a
 * cached answer there is a double insert, a double export or a missed delete.
 * They call qcService.listQcScans() directly and are meant to.
 */
export async function listQcScansCached(
  force = false,
  onFresh?: (scans: IQcScan[]) => void,
): Promise<IQcScan[]> {
  if (!force && fresh(qcScansEntry, QC_SCAN_TTL_MS)) return qcScansEntry.data;

  if (force) {
    clearSnapshots(QC_SCANS_KEY);
  } else {
    // A stale in-memory list is served like the snapshot — see listWipsCached
    // for why "older than the TTL" must not mean "block on 36,000 rows". Only
    // when the caller can take the fresh list; one that cannot waits for it.
    let stale: CacheEntry<IQcScan[]> | null = qcScansEntry;
    if (!stale) {
      // Cold page. Render the snapshot and go and get the truth behind it —
      // the same bargain listWipsCached makes, for the same reason.
      const snap = await readSnapshot<IQcScan[]>(QC_SCANS_KEY);
      if (usableSnapshot(snap, QC_PERSIST_MAX_MS) && Array.isArray(snap.data) && snap.data.length) stale = snap;
    } else if (!onFresh) {
      stale = null;
    }
    if (stale && Array.isArray(stale.data) && stale.data.length) {
      qcScansEntry = stale;
      void (qcRevalidate ??= qcService.listQcScans()
        .then(data => {
          qcScansEntry = { data, at: Date.now() };
          writeSnapshot(QC_SCANS_KEY, qcScansEntry);
          return data;
        })
        .finally(() => { qcRevalidate = null; }))
        .then(data => onFresh?.(data))
        .catch(() => undefined);   // the stale list stands; Refresh is still there
      return stale.data;
    }
  }

  const data = await qcService.listQcScans();
  qcScansEntry = { data, at: Date.now() };
  writeSnapshot(QC_SCANS_KEY, qcScansEntry);
  return data;
}

/**
 * Same result as receivingService.listReceivingScans(), shared the way the QC
 * scans are.
 *
 * WHY IT IS HERE NOW. Mikko, 2026-09-16: "why it took so long to load,
 * whenever i go back to other screen". BQC4's ledger was the one big list in
 * the payroll run's load with NO cache in front of it at all — the QC scans,
 * the slips and the WIPs each had one, so a screen hop re-downloaded the
 * receiving list and nothing else. It is read by Receiving·BQC4, the
 * workcenter hook and the payroll run, which is the same three-screen walk
 * that made the others worth caching.
 *
 * SHORT TTL, NOT PERSISTED — the slips bargain, for the slips reason. The
 * list is APP-OWNED and moves all day, and a receiving scan the app has
 * forgotten is a bundle somebody receives twice.
 *
 * A WRITER MUST NOT READ THIS, exactly as with the QC scans: the receiving
 * outbox and the undo path decide what to write from what is there, and a
 * cached answer is a duplicate or a missed row. They call the service
 * directly and are meant to.
 */
export async function listReceivingScansCached(
  force = false,
  onFresh?: (scans: IReceivingScan[]) => void,
): Promise<IReceivingScan[]> {
  if (!force && fresh(receivingEntry, RECEIVING_TTL_MS)) return receivingEntry.data;
  // Past the TTL, a caller that can take the fresh list is shown the old one
  // at once and handed the real one when it lands (listWipsCached says why).
  // The list is still never PERSISTED — only a screen hop within this tab's
  // memory is served stale, and for as long as one read takes.
  if (!force && onFresh && receivingEntry && receivingEntry.data.length) {
    void (receivingRevalidate ??= receivingService.listReceivingScans()
      .then(data => { receivingEntry = { data, at: Date.now() }; return data; })
      .finally(() => { receivingRevalidate = null; }))
      .then(data => onFresh(data))
      .catch(() => undefined);
    return receivingEntry.data;
  }
  const data = await receivingService.listReceivingScans();
  receivingEntry = { data, at: Date.now() };
  return data;
}

/**
 * A station that has just written to the ledger hands the settled list back so
 * the next screen to open is not served a copy WITHOUT the inspector's own
 * scan. The entry keeps its age: this is not a read, so it must not make the
 * list look fresher than it is — other terminals' scans are still behind it.
 */
export function rememberQcScans(scans: IQcScan[]): void {
  qcScansEntry = { data: scans, at: qcScansEntry?.at ?? 0 };
}
export function rememberReceivingScans(scans: IReceivingScan[]): void {
  receivingEntry = { data: scans, at: receivingEntry?.at ?? 0 };
}
